<template>
  <MenuButton :full-width="fullWidth">
    <template #menu-button-trigger>
      <PickerTrigger
        :label="selected ? labelOf(selected) : (emptyLabel ?? placeholder)"
        :selected="Boolean(selected)"
      />
    </template>

    <!-- Everyone is already on it, or the team is just the caller: say so instead of opening an empty menu -->
    <View v-if="!options.length && !emptyLabel" direction="column" gap="xs" class="empty">
      <Text variant="label-medium">Nobody else to add</Text>
      <Text variant="body-small" color="secondary">Everyone on your team already has this role here.</Text>
    </View>

    <MenuButtonItem
      v-if="emptyLabel"
      :title="emptyLabel"
      icon="users-01"
      :is-active="model === undefined"
      @click="model = undefined"
    />

    <MenuButtonItem
      v-for="member in options"
      :key="member.investorId"
      :title="labelOf(member)"
      :subtitle="subtitleOf(member)"
      :is-active="member.investorId === model"
      @click="model = member.investorId"
    >
      <template #prefix>
        <Avatar :user-name="nameOf(member)" show-single-letter size="small" />
      </template>
    </MenuButtonItem>
  </MenuButton>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { Avatar, MenuButton, MenuButtonItem, PickerTrigger, Text, View } from '@tallo/design-system';

import { companyRoleLabels } from '../enums/company-role.enum';
import { CompanyTeamMemberDto } from '../interfaces/team.dto';
import { teamMemberName as nameOf } from '../utils/team-member-name.util';

const props = withDefaults(
  defineProps<{
    members: CompanyTeamMemberDto[];
    /** Seats that already hold the role are left out entirely */
    excludeIds?: (string | null | undefined)[];
    /** What a seat already is where the pick applies, shown after their company role - "collaborator here" */
    hints?: Record<string, string>;
    placeholder?: string;
    fullWidth?: boolean;
    /** Offers picking nobody, under this label, for a choice that falls back to someone else when empty */
    emptyLabel?: string;
  }>(),
  { excludeIds: () => [], hints: () => ({}), placeholder: 'Choose a team member', fullWidth: true },
);

/** The picked seat's investor id */
const model = defineModel<string | undefined>();

const options = computed(() => props.members.filter((member) => !props.excludeIds.includes(member.investorId)));
const selected = computed(() => props.members.find((member) => member.investorId === model.value));

function subtitleOf(member: CompanyTeamMemberDto): string {
  return [companyRoleLabels[member.role], props.hints[member.investorId]].filter(Boolean).join(' · ');
}

function labelOf(member: CompanyTeamMemberDto): string {
  return member.isYou ? `${nameOf(member)} (you)` : nameOf(member);
}
</script>

<style scoped lang="scss">
.empty {
  max-width: 18rem;
  padding: var(--t-spacing-sm) var(--t-spacing-md);
}
</style>
